import {
  Children,
  cloneElement,
  isValidElement,
  type ReactNode,
  type Ref,
  useMemo,
} from "react";
import { mergeRefs } from "./index";

type AnyProps = Record<string, unknown>;

function mergeProps(slotProps: AnyProps, childProps: AnyProps): AnyProps {
  const result = { ...childProps };

  for (const propName in slotProps) {
    const slotValue = slotProps[propName];
    const childValue = childProps[propName];

    if (/^on[A-Z]/.test(propName) && typeof slotValue === "function") {
      result[propName] = childValue
        ? (...args: unknown[]) => {
            (childValue as Function)(...args);
            slotValue(...args);
          }
        : slotValue;
    } else if (propName === "className") {
      result[propName] = [childValue, slotValue].filter(Boolean).join(" ");
    } else if (propName === "style") {
      result[propName] = {
        ...(childValue as object),
        ...(slotValue as object),
      };
    } else if (childValue === undefined) {
      result[propName] = slotValue;
    }
  }

  return result;
}

type SlotProps = { children: ReactNode } & AnyProps;

/**
 * Slot component that merges props onto its single child element.
 * Enables composition of floating UI components (Tooltip, Popover, Menu).
 *
 * Props cascade through component wrappers down to the actual DOM element.
 */
export const Slot = ({ children, ...slotProps }: SlotProps) => {
  const child = Children.only(children);

  if (!isValidElement(child)) {
    return null;
  }

  const childRef = (child.props as AnyProps).ref as Ref<unknown> | undefined;
  const { ref: slotRef, ...restSlotProps } = slotProps;

  const mergedProps = mergeProps(restSlotProps, child.props as AnyProps);
  const mergedRef = useMemo(() => {
    if (slotRef || childRef) {
      return mergeRefs(slotRef as Ref<unknown>, childRef);
    }
    return undefined;
  }, [slotRef, childRef]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return cloneElement(child, { ...mergedProps, ref: mergedRef } as any);
};
